import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  HeaderCell,
  AccordionSection,
  AccordionHeader,
  AccordionContent,
  Badge,
  HospitalAffiliations,
  AffiliationItem,
  Pill,
  ArrowWrapper,
  GeographicAccessibility,
  PillNumber,
  SearchListWrapper,
  SkeletonWrapper,
  PlaceholderCell,
  WebsiteLink,
} from "./index.styled";
import { getHighestValueIndex, transformDataForFrontend } from "./dataHandler";
import { ReactComponent as AccordionArrowDown } from "../../Assets/Icons/AccordionArrowDown.svg";
import { ReactComponent as StarIcon } from "../../Assets/Icons/Star2Icon.svg";
import { ReactComponent as WarnIcon } from "../../Assets/Icons/WarnIcon.svg";
import SearchList from "../SearchList";
import { TextSkeleton } from "../Skeletons/index.styled";
import { useSelector } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";

// Website cells are links only when they hold a real URL — placeholder text
// like "NOT AVAILABLE" or "-" is rendered as plain text.
const isWebsiteLink = (value) => {
  if (typeof value !== "string") return false;
  const v = value.trim();
  if (!v || v === "-" || v.toUpperCase() === "NOT AVAILABLE") return false;
  return /^https?:\/\//i.test(v) || /^www\./i.test(v) || /\.[a-z]{2,}/i.test(v);
};

const toHref = (value) =>
  /^https?:\/\//i.test(value.trim()) ? value.trim() : `https://${value.trim()}`;

const ComparisonTable = ({
  hospitalsData,
  searchResults,
  isSearchOpen,
  setIsSearchOpen,
  tableLoading,
  scrollRef,
  onScroll,
  showGradient,
}) => {
  const { compareHospitals } = useSelector((state) => state.compare);
  const compareHospitalsCount = (compareHospitals.filter(h => Number(h.provider_number))).length;
  const [headerWidth, setHeaderWidth] = useState(0);

  useEffect(() => {
    const updateScrollWidth = () => {
      if (scrollRef.current) {
        const newWidth = scrollRef.current.scrollWidth;
        setHeaderWidth(newWidth);
      }
    };

    updateScrollWidth();

    const observer = new MutationObserver(updateScrollWidth);
    if (scrollRef.current) {
      observer.observe(scrollRef.current, { childList: true, subtree: true });
    }

    return () => observer.disconnect();
  }, [headerWidth]);

  const dropdownRef = useRef(null);
  const {
    hospitals,
    facilities,
    financials,
    affiliations,
    accreditations,
    oncology_focus,
    oncology_research,
    accessibility_inclusivity,
    quality_of_care,
    patient_experience,
  } = transformDataForFrontend(hospitalsData);

  const sectionRefs = {
    facilities: useRef(null),
    financials: useRef(null),
    accessibility_inclusivity: useRef(null),
    affiliations: useRef(null),
    accreditations: useRef(null),
    oncology_focus: useRef(null),
    // oncology_research: useRef(null),
    quality_of_care: useRef(null),
    patient_experience: useRef(null),
  };

  const [openSections, setOpenSections] = useState({
    facilities: true,
    financials: true,
    affiliations: true,
    accreditations: true,
    oncology_focus: true,
    oncology_research: true,
    accessibility_inclusivity: true,
    quality_of_care: true,
    patient_experience: true,
  });

  const badDebtValues = hospitalsData.map(
    (h) => parseFloat(h?.financials?.bad_debt_percentage?.replace("%", "")), // Remove '%' and convert to number
  );

  const lowestDebtIndex = badDebtValues?.indexOf(Math.max(...badDebtValues));

  const highestRevenueIndex = getHighestValueIndex(hospitalsData, "financials.gross_revenue");
  const highestNetRevenueIndex = getHighestValueIndex(hospitalsData, "financials.net_patient_revenue");
  
  const toggleSection = (section) => {
    const updatedSections = {
      ...openSections,
      [section]: !openSections[section],
    };

    const isAnySectionOpen = Object.values(updatedSections).some(
      (isOpen) => isOpen,
    );

    if (!isAnySectionOpen) {
      const firstSection = Object.keys(updatedSections)[0];
      updatedSections[firstSection] = true;
    }

    setOpenSections(updatedSections);
  };

  const labelRefs = useMemo(() => {
    return [
      ...facilities,
      ...financials,
      ...accessibility_inclusivity,
      ...affiliations,
      ...accreditations,
      ...oncology_focus,
      // ...oncology_research,
      ...quality_of_care,
      ...patient_experience,
    ].reduce((acc, item) => {
      acc[item.id] = React.createRef();
      return acc;
    }, {});
  }, [
    facilities,
    financials,
    accessibility_inclusivity,
    affiliations,
    accreditations,
    oncology_focus,
    // oncology_research,
    quality_of_care,
    patient_experience,
  ]);

  function normalizeAccreditations(data) {
    return data.map((item) => {
      if (item.label === "Accreditations") {
        if (
          item.values.length === 1 &&
          Array.isArray(item.values[0]) &&
          item.values[0].length === 1 &&
          typeof item.values[0][0] === "string"
        ) {
          // Case: [['string,with,commas...']] → split into array
          const splitValues = item.values[0][0].split(",").map((s) => s.trim());
          item.values = [splitValues];
        }
        // Else: Already in correct format [["item1", "item2", ...]]
      }
      return item;
    });
  }

  const normalizedAccreditationsData = normalizeAccreditations(accreditations);

  const highlightedElements = useRef([]);

  const highlightElement = (element) => {
    if (element) {
      const firstChild = element.querySelector("td:first-child");
      if (firstChild) {
        firstChild.style.setProperty("background", "#fef9c3", "important");
      }
      element.style.setProperty("background", "#fef9c3", "important");
      highlightedElements.current.push(element);

      setTimeout(() => {
        if (highlightedElements.current.includes(element)) {
          element.style.removeProperty("background");
          firstChild.style.removeProperty("background");
        }
      }, 3000);
    }
  };

  const handleSectionOpen = (section) => {
    setOpenSections((prevState) => ({
      ...prevState,
      [section]: true,
    }));
  };

  const scrollToSection = (id, sectionKey, section) => {
    if (sectionRefs[sectionKey]?.current) {
      sectionRefs[sectionKey].current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      highlightElement(sectionRefs[sectionKey].current);
    } else {
      console.warn(`Section ref for ${sectionKey} not found`);
    }

    setTimeout(() => {
      if (labelRefs[id]?.current) {
        labelRefs[id].current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        highlightElement(labelRefs[id].current);
      } else {
        console.warn(`Label ref for ${id} not found`);
      }
    }, 500);

    setTimeout(() => {
      handleSectionOpen(section);
    }, 600);
  };

  useEffect(() => {
    return () => {
      highlightedElements.current.forEach((element) => {
        if (element) {
          element.style.backgroundColor = "";
        }
      });
      highlightedElements.current = [];
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsSearchOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsSearchOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const formatValue = (val) => {
    if (val === null || val === undefined || val === "" || val == "-")
      return "-"; // Render "-" for empty values
    // if (typeof val === 'number') return Math.abs(val); // Remove `-` if number
    // if (typeof val === 'string') return val.replace(/^-/, ''); // Remove `-` if string
    if (typeof val === "number") {
      return val < 0 ? `(${Math.abs(val)})` : val; // Wrap negative numbers in parentheses
    }

    if (typeof val === "string") {
      return val.startsWith("-") ? `(${val.slice(1)})` : val; // Wrap negative strings in parentheses
    }
    return val;
  };

  return (
    <TableContainer showGradient={showGradient}>
      {/* <button onClick={() => scrollToSection("cost-ratio", "quality_of_care")}>Go to Bad Debt Percentage</button> */}
      <SearchListWrapper ref={dropdownRef}>
        <SearchList
          options={searchResults}
          customWidth={18}
          onOptionClick={scrollToSection}
          top={0.1}
          isOpen={isSearchOpen}
          customPadding={0.313}
          customBorderRadius={0.938}
        />
      </SearchListWrapper>

      <div
        ref={scrollRef}
        onScroll={onScroll}
        style={{
          overflowX: "scroll",
          whiteSpace: "nowrap",
          scrollbarWidth: "thin",
          height: "calc(100vh - 21.7vw)",
          msOverflowStyle: "none",
        }}
      >
        <AccordionSection ref={sectionRefs.facilities}>
          <AccordionHeader
            width={headerWidth * 0.85}
            onClick={() => toggleSection("facilities")}
          >
            <ArrowWrapper isActive={openSections.facilities}>
              <AccordionArrowDown />
            </ArrowWrapper>
            <div
              style={{
                position: "sticky",
                top: 0,
                left: "1.5%",
                background: "white",
                zIndex: 998,
              }}
            >
              Facilities
            </div>
          </AccordionHeader>
          {openSections.facilities && (
            <AccordionContent>
              <Table columnCount={compareHospitalsCount}>
                {/* <TableHead>
                <TableRow>
                  <HeaderCell index={rowIndex}>Facility</HeaderCell>
                  {hospitals.map((hospital, index) => (
                    <HeaderCell key={index}>{hospital.name}</HeaderCell>
                  ))}
                </TableRow>
              </TableHead> */}
                <TableBody>
                  {facilities.map((row, rowIndex) => (
                    <TableRow key={rowIndex} ref={labelRefs[row.id]}>
                      <HeaderCell index={rowIndex}>
                        {row.label || "Unknown Facility"}
                      </HeaderCell>
                      {row.values.map((value, colIndex) => (
                        <React.Fragment key={colIndex}>
                          {tableLoading[colIndex] ? (
                            <TableCell>
                              <TextSkeleton width={14} height={1.5} />
                            </TableCell>
                          ) : (
                            <TableCell>
                              <AnimatePresence mode="wait">
                                <motion.div
                                  key={value}
                                  initial={{ opacity: 0, x: 40 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  exit={{ opacity: 0, x: -40 }}
                                  transition={{
                                    type: "spring",
                                    stiffness: 300,
                                    damping: 25,
                                  }}
                                  {...(row.id === "website" && {
                                    style: {
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      maxWidth: "100%",
                                    },
                                    title:
                                      value !== undefined && value !== null
                                        ? value
                                        : undefined,
                                  })}
                                >
                                  {row.id === "website" && isWebsiteLink(value) ? (
                                    <WebsiteLink
                                      href={toHref(value)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      {value}
                                    </WebsiteLink>
                                  ) : value !== undefined && value !== null ? (
                                    value
                                  ) : (
                                    "N/A"
                                  )}
                                </motion.div>
                              </AnimatePresence>
                            </TableCell>
                          )}
                        </React.Fragment>
                      ))}
                      {compareHospitalsCount >= 3 && (compareHospitalsCount < 20) && <PlaceholderCell />}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </AccordionContent>
          )}
        </AccordionSection>

        <AccordionSection ref={sectionRefs.financials}>
          <AccordionHeader
            width={headerWidth * 0.9}
            onClick={() => toggleSection("financials")}
          >
            <ArrowWrapper isActive={openSections.financials}>
              <AccordionArrowDown />
            </ArrowWrapper>
            <div
              style={{
                position: "sticky",
                top: 0,
                left: "1.5%",
                background: "white",
                zIndex: 998,
              }}
            >
              Financial & Operational Metrics
            </div>
          </AccordionHeader>
          {openSections.financials && (
            <AccordionContent>
              <Table columnCount={compareHospitalsCount}>
                <TableBody>
                  {financials.map((row, rowIndex) => (
                    <TableRow key={rowIndex} ref={labelRefs[row.id]}>
                      <HeaderCell index={rowIndex}>
                        {row.label || "Unknown Facility"}
                      </HeaderCell>
                      {row.values.map((value, colIndex) => (
                        <React.Fragment key={colIndex}>
                          {tableLoading[colIndex] ? (
                            <TableCell>
                              <TextSkeleton width={14} height={1.5} />
                            </TableCell>
                          ) : (
                            <TableCell
                              isNegative={
                                value?.toString().startsWith("-") &&
                                value !== "-"
                              }
                              isPlaceholder={
                                value === null ||
                                value === undefined ||
                                value === "" ||
                                value === "-"
                              }
                            >
                              <AnimatePresence mode="wait">
                                <motion.div
                                  key={value}
                                  initial={{ opacity: 0, x: 40 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  exit={{ opacity: 0, x: -40 }}
                                  transition={{
                                    type: "spring",
                                    stiffness: 300,
                                    damping: 25,
                                  }}
                                >
                                  {value !== undefined && value !== null
                                    ? formatValue(value)
                                    : "N/A"}{" "}
                                  {(row.label === "Gross Revenue" && colIndex === highestRevenueIndex) ||
                                    (row.label === "Net Patient Revenue" && colIndex === highestNetRevenueIndex) ? (
                                      <Badge bg="#675EF2">
                                        <StarIcon /> Highest
                                      </Badge>
                                    ) : null}
                                  {colIndex == lowestDebtIndex &&
                                    row.label == "Bad Debt Percentage" && (
                                      <Badge bg="#F34D4D">
                                        <WarnIcon /> High
                                      </Badge>
                                    )}
                                </motion.div>
                              </AnimatePresence>
                            </TableCell>
                          )}
                        </React.Fragment>
                      ))}
                      {compareHospitalsCount >= 3 && (compareHospitalsCount < 20) && <PlaceholderCell />}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </AccordionContent>
          )}
        </AccordionSection>

        <AccordionSection ref={sectionRefs.accreditations}>
          <AccordionHeader
            width={headerWidth * 0.85}
            onClick={() => toggleSection("accreditations")}
          >
            <ArrowWrapper isActive={openSections.accreditations}>
              <AccordionArrowDown />
            </ArrowWrapper>
            <div
              style={{
                position: "sticky",
                top: 0,
                left: "1.5%",
                background: "white",
                zIndex: 998,
              }}
            >
              Accreditations
            </div>
          </AccordionHeader>
          {openSections.accreditations && (
            <AccordionContent>
              <Table columnCount={compareHospitalsCount}>
                <TableBody>
                  {normalizedAccreditationsData.map((row, rowIndex) => (
                    <TableRow key={rowIndex} ref={labelRefs[row.id]}>
                      <HeaderCell style={{ verticalAlign: "baseline" }}>
                        {row?.label || "Unknown Facility"}
                      </HeaderCell>
                      {row.values.map((hospitalAffiliations, colIndex) => (
                        <React.Fragment key={colIndex}>
                          {tableLoading[colIndex] ? (
                            <TableCell style={{ verticalAlign: "baseline" }}>
                              <SkeletonWrapper>
                                <TextSkeleton width={14} />
                                <TextSkeleton width={14} />
                                <TextSkeleton width={14} />
                                <TextSkeleton width={14} />
                                <TextSkeleton width={14} />
                              </SkeletonWrapper>
                            </TableCell>
                          ) : (
                            <TableCell style={{ verticalAlign: "baseline" }}>
                              {hospitalAffiliations.length === 0 ? (
                                "-"
                              ) : (
                                <HospitalAffiliations>
                                  {hospitalAffiliations?.map(
                                    (value, colIndex) => (
                                      <AffiliationItem key={colIndex}>
                                        <AnimatePresence mode="wait">
                                          <motion.div
                                            key={value}
                                            initial={{ opacity: 0, x: 40 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: -40 }}
                                            transition={{
                                              type: "spring",
                                              stiffness: 300,
                                              damping: 25,
                                            }}
                                          >
                                            {value !== undefined && value !== null
                                              ? value
                                              : "N/A"}
                                          </motion.div>
                                        </AnimatePresence>
                                      </AffiliationItem>
                                    ),
                                  )}
                                </HospitalAffiliations>
                              )}
                            </TableCell>
                          )}
                        </React.Fragment>
                      ))}
                      {compareHospitalsCount >= 3 && (compareHospitalsCount < 20) && <PlaceholderCell />}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </AccordionContent>
          )}
        </AccordionSection>

        <AccordionSection ref={sectionRefs.affiliations}>
          <AccordionHeader
            width={headerWidth * 0.85}
            onClick={() => toggleSection("affiliations")}
          >
            <ArrowWrapper isActive={openSections.affiliations}>
              <AccordionArrowDown />
            </ArrowWrapper>
            <div
              style={{
                position: "sticky",
                top: 0,
                left: "1.5%",
                background: "white",
                zIndex: 998,
              }}
            >
              Affiliations
            </div>
          </AccordionHeader>
          {openSections.affiliations && (
            <AccordionContent>
              <Table columnCount={compareHospitalsCount}>
                <TableBody>
                  {affiliations.map((row, rowIndex) => (
                    <TableRow key={rowIndex} ref={labelRefs[row.id]}>
                      <HeaderCell index={rowIndex}>
                        {row.label || "Unknown Facility"}
                      </HeaderCell>
                      {row.values.map((value, colIndex) => (
                        <React.Fragment key={colIndex}>
                          {tableLoading[colIndex] ? (
                            <TableCell>
                              <TextSkeleton width={14} />
                            </TableCell>
                          ) : (
                            <TableCell>
                              <AnimatePresence mode="wait">
                                <motion.div
                                  key={value}
                                  initial={{ opacity: 0, x: 40 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  exit={{ opacity: 0, x: -40 }}
                                  transition={{
                                    type: "spring",
                                    stiffness: 300,
                                    damping: 25,
                                  }}
                                >
                                  {value === undefined ? "-" : value}
                                </motion.div>
                              </AnimatePresence>
                            </TableCell>
                          )}
                        </React.Fragment>
                      ))}
                      {compareHospitalsCount >= 3 && (compareHospitalsCount < 20) && <PlaceholderCell />}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </AccordionContent>
          )}
        </AccordionSection>

        {/* <AccordionSection ref={sectionRefs.accreditations}>
          <AccordionHeader width={headerWidth * 0.85} onClick={() => toggleSection("accreditations")}>
            <ArrowWrapper isActive={openSections.accreditations}>
              <AccordionArrowDown />
            </ArrowWrapper>
            <div
              style={{
                position: "sticky",
                top: 0,
                left: '1.5%',
                background: "white",
                zIndex: 998,
              }}
            >
              Accreditations
            </div>
          </AccordionHeader>
          {openSections.accreditations && (
            <AccordionContent>
              <Table columnCount={compareHospitalsCount}>
                <TableBody>
                  {accreditations.map((row, rowIndex) => (
                    <TableRow key={rowIndex} ref={labelRefs[row.id]}>
                      <HeaderCell index={rowIndex}>
                        {row.label || "Unknown Facility"}
                      </HeaderCell>
                      {row.values.map((value, colIndex) => (
                        <React.Fragment key={colIndex}>
                          {tableLoading[colIndex] ? (
                            <TableCell>
                              <TextSkeleton
                                width={2.938}
                                height={1.5}
                                borderRadius={1}
                              />
                            </TableCell>
                          ) : (
                            <TableCell>
                              {value === undefined ? (
                                "-"
                              ) : (
                                <Pill status={value}>
                                  {value ? "Yes" : "No"}
                                </Pill>
                              )}
                            </TableCell>
                          )}
                        </React.Fragment>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </AccordionContent>
          )}
        </AccordionSection> */}

        <AccordionSection ref={sectionRefs.oncology_focus}>
          <AccordionHeader
            width={headerWidth * 0.85}
            onClick={() => toggleSection("oncology_focus")}
          >
            <ArrowWrapper isActive={openSections.oncology_focus}>
              <AccordionArrowDown />
            </ArrowWrapper>
            <div
              style={{
                position: "sticky",
                top: 0,
                left: "1.5%",
                background: "white",
                zIndex: 998,
              }}
            >
              Oncology Focus
            </div>
          </AccordionHeader>
          {openSections.oncology_focus && (
            <AccordionContent>
              <Table columnCount={compareHospitalsCount}>
                <TableBody>
                  {oncology_focus.map((row, rowIndex) => (
                    <TableRow key={rowIndex} ref={labelRefs[row.id]}>
                      <HeaderCell index={rowIndex}>
                        {row.label || "Unknown Facility"}
                      </HeaderCell>
                      {row.values.map((value, colIndex) => (
                        <React.Fragment key={colIndex}>
                          {tableLoading[colIndex] ? (
                            <TableCell>
                              <TextSkeleton
                                width={2.938}
                                height={1.5}
                                borderRadius={1}
                              />
                            </TableCell>
                          ) : (
                            <TableCell>
                              <AnimatePresence mode="wait">
                                <motion.div
                                  key={value}
                                  initial={{ opacity: 0, x: 40 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  exit={{ opacity: 0, x: -40 }}
                                  transition={{
                                    type: "spring",
                                    stiffness: 300,
                                    damping: 25,
                                  }}
                                >
                                  {value === undefined ? "-" : value}
                                </motion.div>
                              </AnimatePresence>
                            </TableCell>
                          )}
                        </React.Fragment>
                      ))}
                      {compareHospitalsCount >= 3 && (compareHospitalsCount < 20) && <PlaceholderCell />}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </AccordionContent>
          )}
        </AccordionSection>

        {/* <AccordionSection ref={sectionRefs.oncology_research}>
          <AccordionHeader width={headerWidth * 0.85} onClick={() => toggleSection("oncology_research")}>
            <ArrowWrapper isActive={openSections.oncology_research}>
              <AccordionArrowDown />
            </ArrowWrapper>
            <div
              style={{
                position: "sticky",
                top: 0,
                left: '1.5%',
                background: "white",
                zIndex: 998,
              }}
            >
              Oncology Research
            </div>
          </AccordionHeader>
          {openSections.oncology_research && (
            <AccordionContent>
              <Table columnCount={compareHospitalsCount}>
                <TableBody>
                  {oncology_research.map((row, rowIndex) => (
                    <TableRow key={rowIndex} ref={labelRefs[row.id]}>
                      <HeaderCell index={rowIndex}>
                        {row.label || "Unknown Facility"}
                      </HeaderCell>
                      {row.values.map((value, colIndex) => (
                        <React.Fragment key={colIndex}>
                          {tableLoading[colIndex] ? (
                            <TableCell>
                              <TextSkeleton
                                width={2.938}
                                height={1.5}
                                borderRadius={1}
                              />
                            </TableCell>
                          ) : (
                            <TableCell>
                              {value === undefined ? (
                                "-"
                              ) : (
                                <Pill status={value}>
                                  {value ? "Yes" : "No"}
                                </Pill>
                              )}
                            </TableCell>
                          )}
                        </React.Fragment>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </AccordionContent>
          )}
        </AccordionSection> */}

        <AccordionSection ref={sectionRefs.quality_of_care}>
          <AccordionHeader
            width={headerWidth * 0.85}
            onClick={() => toggleSection("quality_of_care")}
          >
            <ArrowWrapper isActive={openSections.quality_of_care}>
              <AccordionArrowDown />
            </ArrowWrapper>
            <div
              style={{
                position: "sticky",
                top: 0,
                left: "1.5%",
                background: "white",
                zIndex: 998,
              }}
            >
              Quality of Care
            </div>
          </AccordionHeader>
          {openSections.quality_of_care && (
            <AccordionContent>
              <Table columnCount={compareHospitalsCount}>
                <TableBody>
                  {quality_of_care.map((row, rowIndex) => (
                    <TableRow key={rowIndex} ref={labelRefs[row.id]}>
                      <HeaderCell index={rowIndex}>
                        {row.label || "Unknown Facility"}
                      </HeaderCell>
                      {row.values.map((value, colIndex) => (
                        <React.Fragment key={colIndex}>
                          {tableLoading[colIndex] ? (
                            <TableCell>
                              <TextSkeleton
                                width={2.938}
                                height={1.5}
                                borderRadius={1}
                              />
                            </TableCell>
                          ) : (
                            <TableCell>
                              <AnimatePresence mode="wait">
                                <motion.div
                                  key={value}
                                  initial={{ opacity: 0, x: 40 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  exit={{ opacity: 0, x: -40 }}
                                  transition={{
                                    type: "spring",
                                    stiffness: 300,
                                    damping: 25,
                                  }}
                                >
                          {value === undefined || value === "-" ? (
                                "-"
                              ) : row.label === "NCCN" || row.label === "C4QI" ? (
                                <Pill status={value === "Yes"}>{value}</Pill>
                              ) : row.label === "Oncology Mortality" ||
                                row.label === "Readmissions Rates" ? (
                                <Pill status={value}>{value}</Pill>
                              ) : (
                                <PillNumber
                                  bg={
                                    value > 3.5
                                      ? "#61C676"
                                      : value > 2.5
                                      ? "#F6B51C"
                                      : "#F34D4D"
                                  }
                                >
                                  {value}
                                </PillNumber>
                              )}
                                </motion.div>
                              </AnimatePresence>
                            </TableCell>
                          )}
                        </React.Fragment>
                      ))}
                      {compareHospitalsCount >= 3 && (compareHospitalsCount < 20) && <PlaceholderCell />}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </AccordionContent>
          )}
        </AccordionSection>

        <AccordionSection ref={sectionRefs.patient_experience}>
          <AccordionHeader width={headerWidth * 0.85} onClick={() => toggleSection("patient_experience")}>
            <ArrowWrapper isActive={openSections.patient_experience}>
              <AccordionArrowDown />
            </ArrowWrapper>
            <div
              style={{
                position: "sticky",
                top: 0,
                left: '1.5%',
                background: "white",
                zIndex: 998,
              }}
            >
              Patient Experience
            </div>
          </AccordionHeader>
          {openSections.patient_experience && (
            <AccordionContent>
              <Table columnCount={compareHospitalsCount}>
                <TableBody>
                  {patient_experience.map((row, rowIndex) => (
                    <TableRow key={rowIndex} ref={labelRefs[row.id]}>
                      <HeaderCell index={rowIndex}>
                        {row.label || "Unknown Facility"}
                      </HeaderCell>
                      {row.values.map((value, colIndex) => (
                        <React.Fragment key={colIndex}>
                          {tableLoading[colIndex] ? (
                            <TableCell>
                              <TextSkeleton
                                width={2.938}
                                height={1.5}
                                borderRadius={1}
                              />
                            </TableCell>
                          ) : (
                            <TableCell>
                              {value === undefined || value === "-" ? (
                                "-"
                              ) : row.label === "Cancer Navigation Program" ? (
                                <Pill status={value === "Yes"}>
                                  {value}
                                </Pill>
                              ) : (
                                <PillNumber
                                  bg={
                                    String(value).toLowerCase() === "average"
                                      ? "#F6B51C"
                                      : String(value).toLowerCase() === "low"
                                        ? "#F34D4D"
                                        : "#61C676"
                                  }
                                >
                                  {value}
                                </PillNumber>
                              )}
                            </TableCell>
                          )}
                        </React.Fragment>
                      ))}
                      {compareHospitalsCount >= 3 && (compareHospitalsCount < 20) && <PlaceholderCell />}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </AccordionContent>
          )}
        </AccordionSection>

        <AccordionSection ref={sectionRefs.accessibility_inclusivity}>
          <AccordionHeader
            width={headerWidth * 0.85}
            onClick={() => toggleSection("accessibility_inclusivity")}
          >
            <ArrowWrapper isActive={openSections.accessibility_inclusivity}>
              <AccordionArrowDown />
            </ArrowWrapper>
            <div
              style={{
                position: "sticky",
                top: 0,
                left: "1.5%",
                background: "white",
                zIndex: 998,
              }}
            >
              Accessibility & Inclusivity
            </div>
          </AccordionHeader>
          {openSections.accessibility_inclusivity && (
            <AccordionContent>
              <Table columnCount={compareHospitalsCount}>
                <TableBody>
                  {accessibility_inclusivity.map((row, rowIndex) => (
                    <TableRow key={rowIndex} ref={labelRefs[row.id]}>
                      <HeaderCell index={rowIndex}>
                        {row.label || "Unknown Facility"}
                      </HeaderCell>
                      {row.values.map((value, colIndex) => (
                        <React.Fragment key={colIndex}>
                          {tableLoading[colIndex] ? (
                            <TableCell>
                              <TextSkeleton
                                width={2.938}
                                height={1.5}
                                borderRadius={1}
                              />
                            </TableCell>
                          ) : (
                            <TableCell>
                              <AnimatePresence mode="wait">
                                <motion.div
                                  key={value}
                                  initial={{ opacity: 0, x: 40 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  exit={{ opacity: 0, x: -40 }}
                                  transition={{
                                    type: "spring",
                                    stiffness: 300,
                                    damping: 25,
                                  }}
                                >
                                  {value === undefined || value === "-" ? (
                                    "-"
                                  ) : row.label === "Geographic Accessibility" ? (
                                    <GeographicAccessibility>
                                      {value}
                                    </GeographicAccessibility>
                                  ) : (
                                    <Pill status={value}>
                                      {value ? "Yes" : "No"}
                                    </Pill>
                                  )}
                                </motion.div>
                              </AnimatePresence>
                            </TableCell>
                          )}
                        </React.Fragment>
                      ))}
                      {compareHospitalsCount >= 3 && (compareHospitalsCount < 20) && <PlaceholderCell />}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </AccordionContent>
          )}
        </AccordionSection>
      </div>
    </TableContainer>
  );
};

export default ComparisonTable;
